import { inject, Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { ApcQueryComplexInput, ApcQueryInput, ComparatorOperators, GetProcessCoverageGQL, GetProcessCoverageQuery, OperatorInput, PagingInput, ApcColumns, AssociateProposalToProcessCoverageGQL, ForecastType, AssociateProposalToProcessCoverageMutation, DownloadAuditProcessCoverageGQL, DownloadAuditProcessCoverageMutation, GetRationaleCategoriesGQL, GetRationaleCategoriesQuery, UpdateProcessCoverageStatusMutation, UpdateProcessCoverageStatusGQL, RationaleApprovalStatus, GetProcessCoverageFiltersQuery, GetProcessCoverageFiltersGQL, UpdateForecastCoverageGQL, UpdateForecastCoverageMutation, UpdateProcessCoverageNotesGQL, UpdateProcessCoverageNotesMutation, DownloadAuditProcessCoverageEngagementGQL, DownloadAuditProcessCoverageEngagementMutation } from "../../../graphql/generated";

@Injectable({
    providedIn: 'root'
})
export class ProcessCoverageService {
    private readonly GetProcessCovrageGQL: GetProcessCoverageGQL = inject(GetProcessCoverageGQL);
    private readonly UpdateForecastCoverageGQL: UpdateForecastCoverageGQL = inject(UpdateForecastCoverageGQL);
    private readonly AssociateProposalToProcessCoverageGQL: AssociateProposalToProcessCoverageGQL = inject(AssociateProposalToProcessCoverageGQL);
    private readonly DownloadAuditProcessCoverageGQL: DownloadAuditProcessCoverageGQL = inject(DownloadAuditProcessCoverageGQL);
    private readonly DownloadAuditProcessCoverageEngagementGQL: DownloadAuditProcessCoverageEngagementGQL = inject(DownloadAuditProcessCoverageEngagementGQL);
    private readonly GetRationaleCategoriesGQL: GetRationaleCategoriesGQL = inject(GetRationaleCategoriesGQL);
    private readonly UpdateProcessCoverageStatusGQL: UpdateProcessCoverageStatusGQL = inject(UpdateProcessCoverageStatusGQL);
    private readonly GetProcessCoverageFiltersGQL: GetProcessCoverageFiltersGQL = inject(GetProcessCoverageFiltersGQL);
    private readonly UpdateProcessCoverageNotesGQL: UpdateProcessCoverageNotesGQL = inject(UpdateProcessCoverageNotesGQL)

    getProcesses(parameters: any, legalEntityIds: string[], au: boolean | null, year?: number): Observable<GetProcessCoverageQuery['auditProcessCoverages']> {
        const param = convertLazyLoadToComplexWithPage(parameters);
        return this.GetProcessCovrageGQL.fetch({ legalEntityIds, page: param.page, query: param.query, year }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.auditProcessCoverages))
    }

    updateProcessCoverage(coverage: boolean, forecastId: string, rationaleCategoryId: string | null, rationaleMissingCoverage: string | null): Observable<UpdateForecastCoverageMutation['updateForecastCoverage']> {
        return this.UpdateForecastCoverageGQL.mutate({ coverage, forecastId, rationaleCategoryId, rationaleMissingCoverage }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateForecastCoverage))
    }

    associateProposalToProcessCoverage(processCoverageIds: number[], proposalIds: number[], forecastType: ForecastType): Observable<AssociateProposalToProcessCoverageMutation['associateProposalToProcessCoverage']> {
        return this.AssociateProposalToProcessCoverageGQL.mutate({ forecastType, processCoverageIds, proposalIds }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.associateProposalToProcessCoverage))
    }

    downloadAuditProcessCoverage(legalEntityIds: string[], parameters: any, timeZone: string, year?: number): Observable<DownloadAuditProcessCoverageMutation['downloadAuditProcessCoverage']> {
        const param = convertLazyLoadToComplexWithPage(parameters);
        return this.DownloadAuditProcessCoverageGQL.mutate({ legalEntityIds, query: param.query, timeZone, year }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.downloadAuditProcessCoverage))
    }

    downloadAuditProcessCoverageEngagement(legalEntityIds: string[], parameters: any, timeZone: string, year?: number, format?: string, au?: boolean | null): Observable<DownloadAuditProcessCoverageEngagementMutation['downloadAuditProcessCoverageEngagement']> {
        const param = convertLazyLoadToComplexWithPage(parameters);
        return this.DownloadAuditProcessCoverageEngagementGQL.mutate({ legalEntityIds, query: param.query, timeZone, year, format, au }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.downloadAuditProcessCoverageEngagement))
    }

    getRationaleCategories(): Observable<GetRationaleCategoriesQuery['proposalRationaleCategories']> {
        return this.GetRationaleCategoriesGQL.fetch({}, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.proposalRationaleCategories))
    }

    updateStatus(forecastId: string, centralRationaleReviewNote: string, rationaleApprovalStatus: RationaleApprovalStatus): Observable<UpdateProcessCoverageStatusMutation['updateForecastReview']> {
        return this.UpdateProcessCoverageStatusGQL.mutate({ forecastId, centralRationaleReviewNote, rationaleApprovalStatus }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateForecastReview))
    }

    getProcessCoverageFilters(legalEntityIds: string[], year: number): Observable<GetProcessCoverageFiltersQuery['auditProcessCoverageFieldsValues']> {
        return this.GetProcessCoverageFiltersGQL.fetch({ legalEntityIds, year }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.auditProcessCoverageFieldsValues))
    }

    updateProcessCoverageNotes(apcId: string, notes: string): Observable<UpdateProcessCoverageNotesMutation['updateAuditProcessNotes']> {
        return this.UpdateProcessCoverageNotesGQL.mutate({ apcId, notes }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateAuditProcessNotes))
    }
}

export function convertLazyLoadToComplexWithPage(parameters: any): { query: ApcQueryComplexInput, page: PagingInput } {
    const query: ApcQueryInput[] = [];
    if (parameters) {

        if (parameters.filters) {

            Object.entries(parameters.filters).forEach(([field, values]) => {

                if (values !== null && Array.isArray(values) && values.length > 0) {
                    (ENUM_PROCESS_COVERAGE[field as keyof typeof ENUM_PROCESS_COVERAGE])?.forEach(key => {

                        query.push({
                            field: key as ApcColumns,
                            filters: {
                                operator: [ApcColumns.Level, ApcColumns.ApcMethodologyRequirementMonths, ApcColumns.ApcMethodologyRequirementSafetyMonths].includes(key) ? ComparatorOperators.Eq : ComparatorOperators.Match,
                                value: (key === ApcColumns.LegalEntityCode ? values.map(v => v?.split(/\s-\s(.+)/)[0]?.trim()) : key === ApcColumns.ApcAu ? values.map(v => String(v)?.toLowerCase() === 'yes' ? true : false) : key === ApcColumns.Level ? values.map(v => v?.toLowerCase()?.split('level ')?.[1] ?? v) : key === ApcColumns.ApcLastAuditPlanCoverage ? values.map(v => String(v) === 'Not Covered' ? '1900' : v) : values.map(v => v === 'true' ? true : v === 'false' ? false : v?.toLowerCase() === 'active' ? true : v?.toLowerCase() === 'not active' ? false : v))?.map(v => String(v)?.toLowerCase() === 'empty' ? field?.includes('pCounter') ? '-1' : null : v)
                            }
                        });
                    })
                }
            });
        }
    }
    return {
        query: {
            operator: OperatorInput.And,
            query: [
                {
                    query: query,
                    operator: OperatorInput.And
                }
            ]
        },
        page: {
            pageNo: parameters?.page ?? 0,
            pageSize: parameters?.rows ?? 1000000
        }
    }
}

export const ENUM_PROCESS_COVERAGE = {
    au: [ApcColumns.ApcAu],
    legalEntityName: [ApcColumns.LegalEntityCode],
    level: [ApcColumns.Level],
    code: [ApcColumns.Code],
    name: [ApcColumns.Name],
    residualRisk: [ApcColumns.ApcRr],
    inherentRisk: [ApcColumns.ApcIr],
    internalControlSystem: [ApcColumns.ApcIcs],
    lastAuditClosed: [ApcColumns.ApcLastAuditClosed],
    methodologyRequirementMonths: [ApcColumns.ApcMethodologyRequirementMonths],
    lastRelevantRRVariation: [ApcColumns.ApcLastRelevantRRVariation],
    methodologyRequirementSafetyMonths: [ApcColumns.ApcMethodologyRequirementSafetyMonths],
    referenceDate: [ApcColumns.ApcReferenceDate],
    nextAuditToBePerformed: [ApcColumns.ApcNextAuditToBePerformed],
    lastRRVariationSafetyConstraint: [ApcColumns.ApcLastRRVariationSafetyConstraint],
    referenceDateSafetyConstraint: [ApcColumns.ApcReferenceDateSafetyConstraint],
    nextDeadline4YearCycle: [ApcColumns.ApcNextDeadline_4YearCycle],
    nextAuditToBePerformedWithin: [ApcColumns.ApcNextAuditToBePerformedWithin],
    lastAuditPerformed: [ApcColumns.ApcLastAuditPerformed],
    issuesHighOpened: [ApcColumns.ApcIssuesHighOpened],
    nextExpectedCoverageYear: [ApcColumns.ApcNextExpectedCoverageYear],
    expectedCoverage: [ApcColumns.ApcExpectedCoverage],
    coverageCurrentPlan: [ApcColumns.ApcCoverageCurrentPlan],
    proposalCurrentPlan: [ApcColumns.ApcProposalCurrentPlan],
    rationaleMissingCoverageCurrentPlan: [ApcColumns.ApcRationaleMissingCoverageCurrentPlan],
    centralRationaleReviewNoteCurrentPlan: [ApcColumns.ApcRationaleApprovalNotesCurrentPlan],
    coverageNextPlan: [ApcColumns.ApcCoverageOneYearNextPlan],
    proposalNextPlan: [ApcColumns.ApcCoverageOneYearNextProposal],
    coverageFuturePlan: [ApcColumns.ApcCoverageTwoYearNextPlan],
    topicFuturePlan: [ApcColumns.ApcCoverageTwoYearNextTopic],
    proposalFuturePlan: [ApcColumns.ApcCoverageTwoYearNextProposal],
    notCoverageCurrentPlan: [ApcColumns.ApcMissingCoverageCurrentPlan],
    proposalRationaleCategoryCurrentPlan: [ApcColumns.ApcRationaleCategoryCurrentPlan],
    rationaleApprovalStatusCurrentPlan: [ApcColumns.ApcRationaleApprovalStatusCurrentPlan],
    projectionFirstProposalView: [ApcColumns.ApcFirstProjection],
    projectionSecondProposalView: [ApcColumns.ApcSecondProjection],
    projectionThirdProposalView: [ApcColumns.ApcThirdProjection],
    lastAuditPlanCoverage: [ApcColumns.ApcLastAuditPlanCoverage],
    fiveYearBeforeResidualRisk: [ApcColumns.ApcResidualRisk_01Label],
    fiveYearBeforeApCounter: [ApcColumns.ApcAuditPlan_01Count],
    fourYearBeforeResidualRisk: [ApcColumns.ApcResidualRisk_02Label],
    fourYearBeforeApCounter: [ApcColumns.ApcAuditPlan_02Count],
    threeYearBeforeResidualRisk: [ApcColumns.ApcResidualRisk_03Label],
    threeYearBeforeApCounter: [ApcColumns.ApcAuditPlan_03Count],
    twoYearBeforeResidualRisk: [ApcColumns.ApcResidualRisk_04Label],
    twoYearBeforeApCounter: [ApcColumns.ApcAuditPlan_04Count],
    oneYearBeforeResidualRisk: [ApcColumns.ApcResidualRisk_05Label],
    oneYearBeforeApCounter: [ApcColumns.ApcAuditPlan_05Count],
    apCounter: [ApcColumns.ApcAuditPlan_06Count],
    notes: [ApcColumns.ApcNotes]
}